// ARM-001 — Admin Agents list page.
// Lists every agent across all owners. Search, owner-filter, status toggle.
// Pattern mirrored from Truck-Owners.js for visual + interaction parity.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Avatar,
    Box,
    Chip,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Select,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { getData, putData, API_ENDPOINTS } from "../../services/apiService.js";
import LoadingSpinner from "../LoadingContext";

const getInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
};

const STATUS_TABS = [
    { label: "All", value: null },
    { label: "Active", value: "active" },
    { label: "Suspended", value: "suspended" },
    { label: "Removed", value: "deleted" },
];

const statusChipColor = (s) => {
    switch (s) {
        case "active":
            return { bg: "#D1FAE5", fg: "#065F46", label: "Active" };
        case "suspended":
            return { bg: "#FEF3C7", fg: "#92400E", label: "Suspended" };
        case "deleted":
            return { bg: "#FEE2E2", fg: "#991B1B", label: "Removed" };
        default:
            return { bg: "#F1F5F9", fg: "#475569", label: s || "—" };
    }
};

function Agents() {
    const [agents, setAgents] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [searchInput, setSearchInput] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [tabIdx, setTabIdx] = useState(0);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuAgent, setMenuAgent] = useState(null);

    const navigate = useNavigate();
    const menuOpen = Boolean(menuAnchor);
    const statusFilter = STATUS_TABS[tabIdx].value;

    const fetchAgents = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await getData(API_ENDPOINTS.adminGetAllAgents, {
                page: page + 1,
                limit: perPage,
                ...(appliedSearch ? { search: appliedSearch } : {}),
                ...(statusFilter ? { status: statusFilter } : {}),
            });
            if (res?.status === "SUCCESS" && res?.result?.agents) {
                setAgents(res.result.agents);
                setTotal(res.result.total || 0);
            } else {
                setError(res?.message || "Failed to fetch agents");
            }
        } catch (e) {
            setError("Could not load agents");
        } finally {
            setLoading(false);
        }
    }, [page, perPage, appliedSearch, statusFilter]);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);

    const handleSearch = () => {
        setAppliedSearch(searchInput.trim());
        setPage(0);
    };

    const clearSearch = () => {
        setSearchInput("");
        setAppliedSearch("");
        setPage(0);
    };

    const handleMenuOpen = (e, agent) => {
        setMenuAnchor(e.currentTarget);
        setMenuAgent(agent);
    };
    const handleMenuClose = () => {
        setMenuAnchor(null);
        setMenuAgent(null);
    };

    const handleSetStatus = async (status) => {
        if (!menuAgent) return;
        const id = menuAgent.agent_id;
        handleMenuClose();
        try {
            const res = await putData(
                `${API_ENDPOINTS.adminSetAgentStatus}/${id}`,
                { status }
            );
            if (res?.status === "SUCCESS") {
                setSuccess(`Agent ${status}`);
                fetchAgents();
            } else {
                setError(res?.message || "Update failed");
            }
        } catch (e) {
            setError("Update failed");
        }
    };

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / perPage)), [
        total,
        perPage,
    ]);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
                    Team Members
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    mb: 2,
                    flexWrap: "wrap",
                    alignItems: "center",
                }}
            >
                <TextField
                    placeholder="Search by name / email…"
                    value={searchInput}
                    size="small"
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: searchInput && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={clearSearch}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 320 }}
                />
                <Tabs
                    value={tabIdx}
                    onChange={(_, v) => {
                        setTabIdx(v);
                        setPage(0);
                    }}
                    variant="scrollable"
                >
                    {STATUS_TABS.map((t) => (
                        <Tab key={t.label} label={t.label} />
                    ))}
                </Tabs>
            </Box>

            {loading ? (
                <LoadingSpinner />
            ) : (
                <Box
                    sx={{
                        border: "1px solid #E5E7EB",
                        borderRadius: 2,
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns:
                                "minmax(220px,2fr) minmax(100px,1fr) minmax(160px,1.5fr) minmax(160px,1.5fr) 130px 120px 56px",
                            bgcolor: "#F8FAFC",
                            px: 2,
                            py: 1.5,
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#475569",
                        }}
                    >
                        <Box>Member</Box>
                        <Box>Type</Box>
                        <Box>Owner</Box>
                        <Box>Mobile</Box>
                        <Box>Status</Box>
                        <Box>Created</Box>
                        <Box></Box>
                    </Box>
                    {agents.length === 0 && (
                        <Box sx={{ p: 4, textAlign: "center", color: "#6B7280" }}>
                            No team members found.
                        </Box>
                    )}
                    {agents.map((a) => {
                        const fullName = `${a.first_name || ""} ${a.last_name || ""}`.trim();
                        const ownerName = a.Owner
                            ? `${a.Owner.first_name || ""} ${a.Owner.last_name || ""}`.trim()
                            : "—";
                        const chip = statusChipColor(a.status);
                        return (
                            <Box
                                key={a.agent_id}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "minmax(220px,2fr) minmax(100px,1fr) minmax(160px,1.5fr) minmax(160px,1.5fr) 130px 120px 56px",
                                    px: 2,
                                    py: 1.5,
                                    borderTop: "1px solid #F1F5F9",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    "&:hover": { bgcolor: "#F9FAFB" },
                                }}
                                onClick={() => navigate(`/app/agent-details/${a.agent_id}`)}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <Avatar
                                        src={a.pic || undefined}
                                        sx={{ bgcolor: "#EFF6FF", color: "#3E82F7", width: 36, height: 36 }}
                                    >
                                        {getInitials(fullName)}
                                    </Avatar>
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                                            {fullName || "—"}
                                        </Typography>
                                        {a.email && (
                                            <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                                                {a.email}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                <Chip
                                    label={a.agent_type === "manager" ? "Manager" : "Agent"}
                                    size="small"
                                    sx={{
                                        bgcolor: a.agent_type === "manager" ? "#EDE9FE" : "#E0F2FE",
                                        color: a.agent_type === "manager" ? "#5B21B6" : "#0369A1",
                                        fontWeight: 600,
                                        width: "fit-content",
                                    }}
                                />
                                <Box sx={{ fontSize: 13, color: "#1F2937" }}>{ownerName}</Box>
                                <Box sx={{ fontSize: 13 }}>+{a.mobile_num}</Box>
                                <Chip
                                    label={chip.label}
                                    size="small"
                                    sx={{
                                        bgcolor: chip.bg,
                                        color: chip.fg,
                                        fontWeight: 600,
                                        width: "fit-content",
                                    }}
                                />
                                <Box sx={{ fontSize: 12, color: "#6B7280" }}>
                                    {a.createdAt ? new Date(a.createdAt).toLocaleDateString() : "—"}
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleMenuOpen(e, a);
                                    }}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    mt: 2,
                    gap: 2,
                }}
            >
                <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                    Rows per page:
                </Typography>
                <Select
                    value={perPage}
                    size="small"
                    onChange={(e) => {
                        setPerPage(Number(e.target.value));
                        setPage(0);
                    }}
                >
                    {[10, 25, 50, 100].map((n) => (
                        <MenuItem key={n} value={n}>
                            {n}
                        </MenuItem>
                    ))}
                </Select>
                <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                    {total === 0 ? "0 of 0" : `${page * perPage + 1}–${Math.min((page + 1) * perPage, total)} of ${total}`}
                </Typography>
                <IconButton
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                    <KeyboardArrowLeft />
                </IconButton>
                <IconButton
                    disabled={page + 1 >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                >
                    <KeyboardArrowRight />
                </IconButton>
            </Box>

            <Menu anchorEl={menuAnchor} open={menuOpen} onClose={handleMenuClose}>
                {menuAgent?.status !== "active" && (
                    <MenuItem onClick={() => handleSetStatus("active")}>
                        Activate
                    </MenuItem>
                )}
                {menuAgent?.status === "active" && (
                    <MenuItem onClick={() => handleSetStatus("suspended")}>
                        Suspend
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => handleSetStatus("deleted")}
                    sx={{ color: "#DC2626" }}
                >
                    Remove
                </MenuItem>
            </Menu>
        </Box>
    );
}

export default Agents;
